import * as React from 'react'
import DropDownChevron from '../../../assets/icons_new/DropDown.svg'
import CheckedColor from '../../../assets/icons_new/CheckedColor.svg'
import { FaIcon, faUsersGroupSolid, faUsersMedicalSolid } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import AddCompanyGroupModal from '../modals/groups/AddCompanyGroupModal'
import EditCompanyGroupModal from '../modals/groups/EditCompanyGroupModal'
import { CompanyGroupModel } from '../types/companyGroups'
import useCompanyGroupService from '../services/CompanyGroupService'

type CompanyGroupPickerProps = {
  companyId: string
  name?: string
  onSelect?: (option: CompanyGroupModel | null) => void
  chevronIcon?: string
  actualGroup?: CompanyGroupModel | null
}

const CompanyGroupPicker: React.FC<CompanyGroupPickerProps> = ({
  companyId,
  name,
  onSelect,
  chevronIcon = DropDownChevron,
  actualGroup = null,
}) => {
  const { t } = useLocalize()
  const displayName = name || t('common.all')
  const { getGroups } = useCompanyGroupService()
  const [open, setOpen] = React.useState(false)
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = React.useState(false)
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<CompanyGroupModel | null>(actualGroup)
  const [groups, setGroups] = React.useState<CompanyGroupModel[]>([])
  const dropdownRef = React.useRef<HTMLUListElement>(null)

  const fetchGroups = React.useCallback(async () => {
    const fetchedGroups = await getGroups({ companyId })
    setGroups(fetchedGroups)
  }, [companyId])

  React.useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  React.useEffect(() => {
    setSelected(actualGroup)
  }, [actualGroup])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const handleSelect = (option: CompanyGroupModel | null) => {
    setSelected(option)
    setOpen(false)
    onSelect?.(option)
  }

  const allOption: CompanyGroupModel = { id: 0, name: t('common.all') }
  const activeOption: CompanyGroupModel = { id: -1, name: t('addCompany.activeCompanies') }
  const inactiveOption: CompanyGroupModel = { id: -2, name: t('addCompany.inactiveCompanies') }

  React.useEffect(() => {
    if (!actualGroup && !selected) {
      setSelected(activeOption)
      onSelect?.(activeOption)
    }
  }, [])

  const customGroups = groups.filter((opt) => opt.id > 0)

  const fullOptions = [
    allOption,
    activeOption,
    inactiveOption,
    ...customGroups,
  ]

  const filteredOptions = fullOptions.filter(
    (opt) => opt.id !== (selected?.id ?? 0),
  )

  const handleGroupAdded = () => {
    fetchGroups()
    setIsAddGroupModalOpen(false)
  }

  const handleGroupUpdated = () => {
    fetchGroups()
    setIsEditGroupModalOpen(false)
  }

  return (
    <div className="tw-relative tw-z-[200] tw-w-fit tw-flex-shrink-0">
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen((prev) => !prev)
        }}
        className={`tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-bg-transparent tw-px-2 tw-py-1 tw-text-base-content hover:tw-bg-[#F5F5F5] ${
          open
            ? 'tw-border-2 tw-border-primary'
            : 'tw-border tw-border-neutral/20'
        }`}
      >
        <p className="tw-text-sm">{selected?.name ?? displayName}</p>
        <img
          src={chevronIcon}
          alt="dropdown icon"
          className={`tw-ml-2 tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 ${
            open ? 'tw-rotate-180' : ''
          }`}
        />
      </button>

      <ul
        ref={dropdownRef}
        className={`tw-absolute tw-left-0 tw-z-50 tw-mt-2 tw-w-fit tw-min-w-[200px] tw-max-w-[400px] tw-overflow-hidden tw-rounded tw-bg-white tw-p-1.5 tw-shadow-md tw-transition-all tw-duration-200 ${
          open
            ? 'tw-scrollbar-thin tw-max-h-[500px] tw-overflow-y-auto tw-opacity-100'
            : 'tw-max-h-0 tw-opacity-0'
        }`}
      >
        {selected ? (
          <li
            key={selected.id}
            className="tw-font-sm tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-text-ellipsis tw-rounded tw-bg-[#E9EDF9] tw-px-1 tw-py-2"
            onClick={() => handleSelect(selected)}
          >
            <p>{selected.name}</p>
            <img src={CheckedColor} alt="Checked Icon" />
          </li>
        ) : (
          <li
            key={allOption.id}
            className="tw-font-sm tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-text-ellipsis tw-rounded tw-bg-[#E9EDF9] tw-px-1 tw-py-2"
            onClick={() => handleSelect(allOption)}
          >
            <p>{allOption.name}</p>
            <img src={CheckedColor} alt="Checked Icon" />
          </li>
        )}

        <li className="tw-mb-1.5 tw-mt-2.5 tw-border-t tw-border-gray-200"></li>

        {filteredOptions.map((option) => (
          <li
            key={option.id}
            className="tw-font-sm tw-cursor-pointer tw-text-ellipsis tw-rounded tw-px-1 tw-py-2 hover:tw-bg-gray-100"
            onClick={() => handleSelect(option.id === 0 ? null : option)}
          >
            {option.name}
          </li>
        ))}

        <div className="tw-sticky tw-bottom-0 tw-bg-inherit">
          <li className="tw-my-1 tw-border-t tw-border-gray-200"></li>
          <li
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded tw-px-1 tw-py-2 tw-text-gray-500 hover:tw-bg-gray-100"
            onClick={() => {
              handleSelect(null)
              setIsAddGroupModalOpen(true)
            }}
          >
            <FaIcon icon={faUsersMedicalSolid} className="tw-text-primary" />
            <p className="tw-text-nowrap tw-text-sm tw-font-medium tw-text-primary">
              {t('addCompany.addNewGroup')}
            </p>
          </li>
          <li
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2 tw-rounded tw-px-1 tw-py-2 tw-text-gray-500 hover:tw-bg-gray-100"
            onClick={() => {
              handleSelect(null)
              setIsEditGroupModalOpen(true)
            }}
          >
            <FaIcon icon={faUsersGroupSolid} className="tw-text-primary" />
            <p className="tw-text-nowrap tw-text-sm tw-font-medium tw-text-primary">
              {t('addCompany.editGroups')}
            </p>
          </li>
        </div>
      </ul>

      {isEditGroupModalOpen && (
        <EditCompanyGroupModal
          companyId={companyId}
          close={handleGroupUpdated}
        />
      )}

      {isAddGroupModalOpen && (
        <AddCompanyGroupModal
          companyId={companyId}
          close={() => setIsAddGroupModalOpen(false)}
          onGroupAdded={handleGroupAdded}
        />
      )}
    </div>
  )
}

export default CompanyGroupPicker
